/**
 * FloatingAIChat - Persistent AI chat widget available across all pages
 * Inspired by AskYourHOA.com's always-visible chat bubble UX
 */
import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  Bot,
  User,
  Loader2,
  ChevronDown,
  FileText,
  BookOpen,
  HelpCircle,
  DollarSign,
  Calendar,
  Home,
  Minimize2,
  History,
  Plus,
  Trash2
} from "lucide-react";

// Quick question templates for common HOA queries
const QUICK_QUESTIONS = [
  { icon: <Home className="w-4 h-4" />, text: "What are the fence rules?", category: "rules" },
  { icon: <DollarSign className="w-4 h-4" />, text: "What's my balance?", category: "account" },
  { icon: <Calendar className="w-4 h-4" />, text: "When is the next meeting?", category: "meetings" },
  { icon: <FileText className="w-4 h-4" />, text: "How do I submit a DRC request?", category: "drc" },
  { icon: <HelpCircle className="w-4 h-4" />, text: "What are the parking rules?", category: "rules" },
];

const FloatingAIChat = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [ragStats, setRagStats] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessionList, setSessionList] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch RAG stats for AI training progress indicator
  useEffect(() => {
    if (token) {
      fetchRagStats();
    }
  }, [token]);

  // Load persisted chat session on mount
  useEffect(() => {
    if (token && !historyLoaded) {
      loadActiveSession();
    }
  }, [token]);

  const loadActiveSession = async () => {
    try {
      const res = await axios.get(`${API}/chat-sessions/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.messages?.length > 0) {
        setMessages(res.data.messages);
        setSessionId(res.data.session_id);
      }
    } catch (error) {
      // Silently fail - first time or no session
    } finally {
      setHistoryLoaded(true);
    }
  };

  const loadSessionList = async () => {
    try {
      const res = await axios.get(`${API}/chat-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionList(res.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const switchSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/chat-sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setMessages(res.data.messages || []);
        setSessionId(res.data.session_id);
        setShowHistory(false);
      }
    } catch (error) {
      toast.error("Failed to load session");
    }
  };

  const startNewChat = async () => {
    try {
      const res = await axios.post(`${API}/chat-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      setSessionId(res.data.session_id);
      setShowHistory(false);
    } catch (error) {
      toast.error("Failed to create session");
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/chat-sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionList(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        setMessages([]);
        setSessionId(null);
      }
    } catch (error) { /* ignore */ }
  };

  const persistMessage = async (sid, message) => {
    try {
      await axios.post(`${API}/chat-sessions/${sid}/messages`, message, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail - chat still works without persistence
    }
  };

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    try {
      const res = await axios.post(`${API}/chat-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newId = res.data.session_id;
      setSessionId(newId);
      return newId;
    } catch (error) {
      return null;
    }
  };

  // Listen for custom events to open chat with a question
  useEffect(() => {
    const handleOpenChat = (event) => {
      setIsOpen(true);
      setIsMinimized(false);
      if (event.detail?.question) {
        // Small delay to ensure chat is open before sending
        setTimeout(() => {
          sendMessage(event.detail.question);
        }, 100);
      }
    };
    
    window.addEventListener('openAIChat', handleOpenChat);
    return () => window.removeEventListener('openAIChat', handleOpenChat);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const fetchRagStats = async () => {
    try {
      const res = await axios.get(`${API}/rag/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRagStats(res.data.documents);
    } catch (error) {
      // Silently fail - stats are optional
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    // Ensure we have a persistent session
    const sid = await ensureSession();

    // Persist user message
    if (sid) persistMessage(sid, userMessage);

    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        null,
        {
          params: {
            message: text,
            session_id: sid || sessionId,
            context_type: "general"
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const assistantMessage = {
        role: "assistant",
        content: res.data.response,
        timestamp: new Date().toISOString(),
        sources: res.data.sources || [],
        disclaimer: res.data.disclaimer
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (res.data.session_id && !sessionId) {
        setSessionId(res.data.session_id);
      }

      // Persist assistant message
      if (sid) persistMessage(sid, assistantMessage);
      
      // Focus input for follow-up question
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      toast.error("Failed to get AI response");
      const errorMessage = {
        role: "assistant",
        content: "I'm sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault();
      if (input.trim()) {
        sendMessage(input);
      }
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/chat-sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) { /* ignore */ }
    }
    setMessages([]);
    setSessionId(null);
  };

  // Don't render if not logged in
  if (!user || !token) return null;

  // Minimized state - just show the bubble
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110"
        data-testid="floating-chat-button"
        aria-label="Open AI Chat"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap">
            Ask Your HOA AI
          </div>
        </div>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isMinimized ? "w-72" : "w-96"
      }`}
      data-testid="floating-chat-panel"
    >
      <Card className="shadow-2xl border-0 overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-primary to-primary/80 text-white py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">Ask Your HOA</CardTitle>
                {ragStats && (
                  <p className="text-xs text-white/70">
                    AI trained on {ragStats.total_documents || 0} docs
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadSessionList(); }}
                data-testid="chat-history-btn"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={startNewChat}
                data-testid="chat-new-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <ChevronDown className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Content - hidden when minimized */}
        {!isMinimized && (
          <CardContent className="p-0">
            {/* Session History Panel */}
            {showHistory && (
              <div className="border-b bg-gray-50 max-h-48 overflow-y-auto" data-testid="chat-history-panel">
                <div className="p-2">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Chat History</p>
                  {sessionList.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3">No previous chats</p>
                  ) : (
                    sessionList.map((s) => (
                      <div
                        key={s.session_id}
                        className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-xs ${s.session_id === sessionId ? "bg-primary/10" : ""}`}
                        onClick={() => switchSession(s.session_id)}
                        data-testid={`session-item-${s.session_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{s.last_message || "New conversation"}</p>
                          <p className="text-muted-foreground">{new Date(s.updated_at || s.created_at).toLocaleDateString()}</p>
                        </div>
                        <button
                          onClick={(e) => deleteSession(s.session_id, e)}
                          className="p-1 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3 text-muted-foreground hover:text-red-500" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            {/* Messages Area */}
            <ScrollArea className="h-80">
              <div className="p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="space-y-4">
                  {/* Welcome Message */}
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Bot className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">Hi, I&apos;m your HOA AI Assistant!</h3>
                    <p className="text-sm text-muted-foreground">
                      Ask me anything about your community rules, dues, or requests.
                    </p>
                  </div>

                  {/* Quick Questions */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Quick Questions</p>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_QUESTIONS.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => handleQuickQuestion(q.text)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
                          data-testid={`quick-question-${i}`}
                        >
                          {q.icon}
                          <span className="text-gray-700">{q.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.role === "assistant" && (
                        <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                          msg.role === "user"
                            ? "bg-primary text-white"
                            : msg.isError
                            ? "bg-red-50 text-red-700"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        
                        {/* Source Citations */}
                        {msg.sources && msg.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-500 mb-1">Based on:</p>
                            {msg.sources.map((src, j) => (
                              <Badge key={j} variant="outline" className="text-xs mr-1 mb-1">
                                <FileText className="w-3 h-3 mr-1" />
                                {src.title || src.document_type}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      {msg.role === "user" && (
                        <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Loading indicator */}
                  {loading && (
                    <div className="flex gap-2 justify-start">
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-gray-100 rounded-2xl px-4 py-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </ScrollArea>

            {/* Input Area - Always visible when chat is open */}
            <div className="p-3 border-t bg-gray-50">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={messages.length > 0 ? "Ask a follow-up question..." : "Ask about HOA rules, dues, meetings..."}
                  className="flex-1 bg-white"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  size="icon"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || loading}
                  data-testid="chat-send-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Clear chat link */}
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  className="text-xs text-muted-foreground hover:text-gray-700 mt-2 block"
                >
                  Clear conversation
                </button>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default FloatingAIChat;
